import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import type { FC } from 'react';
import { SIMPLE_INPUT_TEST_IDS } from './constants';
import type { ISimpleInputProps } from './types';
import InputError from '~/components/feedback/InputError';
import { getInputSizeClasses } from '~/utils/form';

const SimpleInput: FC<ISimpleInputProps> = ({
	name,
	label,
	width,
	type,
	required,
	description,
	...rest
}) => {
	const [field, meta] = useField(name);
	const { submitCount } = useFormikContext();
	const isError = (meta.touched || submitCount > 0) && !!meta.error;

	return (
		<div className={clsx(getInputSizeClasses(width))}>
			<div
				className="simple-input-container mx-auto rounded-tl-input rounded-tr-input relative h-input w-full"
				data-testid={SIMPLE_INPUT_TEST_IDS.ROOT}
			>
				<input
					{...rest}
					{...field}
					type={type}
					className={clsx(
						'simple-input-input peer bg-transparent placeholder-transparent text-base w-full outline-none pl-5 pr-5 pt-6 pb-1 h-full',
						isError ? 'is-error' : '',
					)}
					id={name}
					aria-invalid={isError}
					aria-describedby={`${name}_error`}
					placeholder=" "
					data-testid={SIMPLE_INPUT_TEST_IDS.INPUT}
				/>
				<label
					htmlFor={name}
					className="simple-input-label pointer-events-none transition-all peer-focus:top-3 top-3 peer-focus:text-xs text-xs absolute left-5 peer-placeholder-shown:top-6 peer-placeholder-shown:font-large-body"
					data-testid={SIMPLE_INPUT_TEST_IDS.LABEL}
				>
					{label}
					{required && !label.endsWith('*') ? <sup>*</sup> : null}
				</label>
				<div className="simple-input-border absolute -bottom-[2px] left-0 w-full h-[2px] transition-colors duration-200" />
				<InputError name={name} isError={isError}>
					{meta.error}
				</InputError>
			</div>
			{description && <p className="font-small-body text-left mt-1">{description}</p>}
		</div>
	);
};

export default SimpleInput;
